import React, { useEffect, useState } from 'react';
import { lendersAPI } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Mail, Phone, Edit, Settings, Trash2, AlertTriangle, Globe, Upload, FileText, Brain, Loader2, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import ProductMatrix from '@/components/lenders/ProductMatrix';
import ProgramReviewTable from '@/components/lenders/ProgramReviewTable';
import NotesProgramsPanel from '@/components/lenders/NotesProgramsPanel';
import GuidelineSearchBar from '@/components/lenders/GuidelineSearchBar';
import { useOutletContext } from 'react-router-dom';

const categories = [
  { id: 'all', label: 'All Lenders' },
  { id: 'Fix & Flip', label: 'Fix & Flip' },
  { id: 'Commercial', label: 'Commercial' },
  { id: 'DSCR', label: 'DSCR' },
  { id: 'Hard Money', label: 'Hard Money' },
  { id: 'Business Loans', label: 'Business Loans/LOC' },
];

const Lenders = () => {
  const outletCtx = useOutletContext() || {};
  const isAdmin = outletCtx.user?.role === 'admin';
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedLender, setSelectedLender] = useState(null);
  const [editingLender, setEditingLender] = useState(null);
  const [formData, setFormData] = useState({
    lender_name: '',
    rep_name: '',
    email: '',
    phone: '',
    website: '',
    categories: [],
    loan_programs: [],
    property_types: [],
    min_fico: '',
    notes: '',
    product_guidelines: ''
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, lender: null });
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [parsingDoc, setParsingDoc] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState({});

  useEffect(() => {
    fetchLenders();
  }, [activeCategory]);

  const fetchLenders = async () => {
    try {
      const category = activeCategory === 'all' ? undefined : activeCategory;
      const response = await lendersAPI.getAll(category);
      setLenders(response.data);
    } catch (error) {
      toast.error('Failed to load lenders');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        categories: typeof formData.categories === 'string' 
          ? formData.categories.split(',').map(c => c.trim()).filter(Boolean)
          : formData.categories,
        loan_programs: typeof formData.loan_programs === 'string'
          ? formData.loan_programs.split(',').map(p => p.trim()).filter(Boolean)
          : formData.loan_programs,
        property_types: typeof formData.property_types === 'string'
          ? formData.property_types.split(',').map(t => t.trim()).filter(Boolean)
          : formData.property_types,
        min_fico: formData.min_fico ? parseInt(formData.min_fico) : null
      };
      
      if (editingLender) {
        await lendersAPI.update(editingLender.lender_id, data);
        toast.success('Lender updated successfully');
      } else {
        await lendersAPI.create(data);
        toast.success('Lender added successfully');
      }
      
      setShowDialog(false);
      resetForm();
      fetchLenders();
    } catch (error) {
      toast.error(editingLender ? 'Failed to update lender' : 'Failed to add lender');
    }
  };

  const handleEdit = (lender) => {
    setEditingLender(lender);
    setFormData({
      lender_name: lender.lender_name,
      rep_name: lender.rep_name,
      email: lender.email,
      phone: lender.phone,
      website: lender.website || '',
      categories: lender.categories.join(', '),
      loan_programs: lender.loan_programs.join(', '),
      property_types: lender.property_types.join(', '),
      min_fico: lender.min_fico?.toString() || '',
      notes: lender.notes || '',
      product_guidelines: lender.product_guidelines || ''
    });
    setShowDialog(true);
  };

  const handleViewDetails = (lender) => {
    setSelectedLender(lender);
    setShowDetailDialog(true);
  };

  const refreshSelected = async () => {
    const updated = await lendersAPI.getOne(selectedLender.lender_id);
    setSelectedLender(updated.data);
    fetchLenders();
  };

  const openLenderById = async (lenderId) => {
    const updated = await lendersAPI.getOne(lenderId);
    setSelectedLender(updated.data);
    setShowDetailDialog(true);
  };

  const handleDeleteLender = async (lenderId) => {
    try {
      await lendersAPI.delete(lenderId);
      toast.success('Lender deleted successfully');
      setDeleteDialog({ open: false, lender: null });
      fetchLenders();
    } catch (error) {
      toast.error('Failed to delete lender');
    }
  };

  const handleProductMatrixUpdate = async (lenderId, updatedPrograms) => {
    try {
      const lender = lenders.find(l => l.lender_id === lenderId);
      await lendersAPI.update(lenderId, {
        ...lender,
        product_matrix: updatedPrograms
      });
      fetchLenders();
      if (selectedLender?.lender_id === lenderId) {
        const updated = await lendersAPI.getOne(lenderId);
        setSelectedLender(updated.data);
      }
      toast.success('Product matrix updated');
    } catch (error) {
      toast.error('Failed to update product matrix');
    }
  };

  const handlePdfUpload = async (lenderId, file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }

    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await lendersAPI.uploadProgram(lenderId, formData);
      toast.success('Program PDF uploaded successfully');
      
      // Refresh lender data
      const updated = await lendersAPI.getOne(lenderId);
      setSelectedLender(updated.data);
      fetchLenders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleParseProgram = async (lenderId, docId) => {
    setParsingDoc(docId);
    try {
      const response = await lendersAPI.parseProgram(lenderId, docId);
      toast.success(response.data.message || `AI found ${response.data.programs_found} loan program(s)`);
      
      // Refresh lender data
      const updated = await lendersAPI.getOne(lenderId);
      setSelectedLender(updated.data);
      fetchLenders();
    } catch (error) {
      toast.error('Failed to parse PDF with AI');
    } finally {
      setParsingDoc(null);
    }
  };

  const handleDeleteProgram = async (lenderId, docId) => {
    try {
      await lendersAPI.deleteProgram(lenderId, docId);
      toast.success('Program document deleted');
      
      const updated = await lendersAPI.getOne(lenderId);
      setSelectedLender(updated.data);
      fetchLenders();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const resetForm = () => {
    setFormData({
      lender_name: '',
      rep_name: '',
      email: '',
      phone: '',
      website: '',
      categories: [],
      loan_programs: [],
      property_types: [],
      min_fico: '',
      notes: '',
      product_guidelines: ''
    });
    setEditingLender(null);
  };

  const toggleNotes = (lenderId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [lenderId]: !prev[lenderId]
    }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="lenders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-primary">Lenders Directory</h1>
          <p className="text-muted-foreground mt-2">Browse lenders by loan category</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-lender-button">
              <Plus className="h-4 w-4" />
              Add Lender
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingLender ? 'Edit Lender' : 'Add New Lender'}</DialogTitle>
              <DialogDescription>Enter the lender information below</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lender_name">Lender Name *</Label>
                  <Input
                    id="lender_name"
                    value={formData.lender_name}
                    onChange={(e) => setFormData({ ...formData, lender_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rep_name">Representative Name *</Label>
                  <Input
                    id="rep_name"
                    value={formData.rep_name}
                    onChange={(e) => setFormData({ ...formData, rep_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://www.example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="categories">Categories (comma-separated) *</Label>
                <Input
                  id="categories"
                  value={formData.categories}
                  onChange={(e) => setFormData({ ...formData, categories: e.target.value })}
                  placeholder="Fix & Flip, Commercial, DSCR"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="loan_programs">Loan Programs (comma-separated) *</Label>
                <Input
                  id="loan_programs"
                  value={formData.loan_programs}
                  onChange={(e) => setFormData({ ...formData, loan_programs: e.target.value })}
                  placeholder="Bridge, DSCR, Ground Up"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="property_types">Property Types (comma-separated) *</Label>
                <Input
                  id="property_types"
                  value={formData.property_types}
                  onChange={(e) => setFormData({ ...formData, property_types: e.target.value })}
                  placeholder="Residential, Multifamily, Commercial"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="min_fico">Minimum FICO</Label>
                <Input
                  id="min_fico"
                  type="number"
                  value={formData.min_fico}
                  onChange={(e) => setFormData({ ...formData, min_fico: e.target.value })}
                  placeholder="650"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="General notes about this lender..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="product_guidelines">Product Guidelines & Offerings</Label>
                <Textarea
                  id="product_guidelines"
                  value={formData.product_guidelines}
                  onChange={(e) => setFormData({ ...formData, product_guidelines: e.target.value })}
                  placeholder="Enter detailed product guidelines, loan criteria, rates, terms, special programs, restrictions, etc. The AI will search through this to recommend lenders for leads..."
                  rows={6}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  💡 The AI assistant will search these guidelines when recommending lenders for leads
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit">{editingLender ? 'Update Lender' : 'Add Lender'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <GuidelineSearchBar isAdmin={isAdmin} onOpenLender={openLenderById} onScanned={fetchLenders} />

      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList>
          {categories.map((cat) => (
            <TabsTrigger key={cat.id} value={cat.id} data-testid={`category-${cat.id}`}>
              {cat.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {categories.map((cat) => (
          <TabsContent key={cat.id} value={cat.id}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
              {lenders.length === 0 ? (
                <div className="col-span-full text-center py-12 text-muted-foreground">
                  No lenders found in this category
                </div>
              ) : (
                lenders.map((lender, index) => (
                  <Card key={lender.lender_id} className="hover:shadow-lg transition-shadow" data-testid={`lender-card-${index}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{lender.lender_name}</CardTitle>
                          <p className="text-sm text-muted-foreground">{lender.rep_name}</p>
                          {lender.product_matrix && lender.product_matrix.length > 0 && (
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                              {lender.product_matrix.length} loan program{lender.product_matrix.length > 1 ? 's' : ''}
                            </p>
                          )}
                          {lender.notes_extraction?.status === 'pending_review' && (
                            <p className="text-xs text-amber-700 mt-1" data-testid={`pending-review-${lender.lender_id}`}>
                              {lender.notes_extraction.programs?.length || 0} program(s) from Notes awaiting approval
                            </p>
                          )}
                          {lender.program_documents && lender.program_documents.length > 0 && (
                            <p className="text-xs text-blue-600 dark:text-blue-400">
                              {lender.program_documents.length} PDF{lender.program_documents.length > 1 ? 's' : ''} uploaded
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleViewDetails(lender)}
                            title="View Details & Programs"
                            data-testid={`view-lender-detail-${lender.lender_id}`}
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEdit(lender)}
                            data-testid={`edit-lender-${index}`}
                            title="Edit Info"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setDeleteDialog({ open: true, lender })}
                            className="text-destructive hover:text-destructive"
                            title="Delete Lender"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Mail className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{lender.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Phone className="h-4 w-4 flex-shrink-0" />
                          <span>{lender.phone}</span>
                        </div>
                        {lender.website && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Globe className="h-4 w-4 flex-shrink-0" />
                            <a 
                              href={lender.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="truncate text-primary hover:underline"
                            >
                              {lender.website.replace(/^https?:\/\//, '')}
                            </a>
                          </div>
                        )}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {lender.categories.map((cat, i) => (
                            <span key={i} className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                              {cat}
                            </span>
                          ))}
                        </div>
                        {lender.min_fico && (
                          <p className="text-muted-foreground">Min FICO: {lender.min_fico}</p>
                        )}
                        {lender.notes && (
                          <div className="mt-2 pt-2 border-t">
                            <button 
                              onClick={() => toggleNotes(lender.lender_id)}
                              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                            >
                              {expandedNotes[lender.lender_id] ? (
                                <><ChevronUp className="h-3 w-3" /> Hide Notes</>
                              ) : (
                                <><ChevronDown className="h-3 w-3" /> Show Notes</>
                              )}
                            </button>
                            {expandedNotes[lender.lender_id] && (
                              <p className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap bg-muted p-2 rounded">
                                {lender.notes}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Lender Detail Dialog with Programs */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedLender && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">{selectedLender.lender_name}</DialogTitle>
                <DialogDescription className="flex flex-col gap-1">
                  <span>{selectedLender.rep_name}</span>
                  <span className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Mail className="h-4 w-4" /> {selectedLender.email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Phone className="h-4 w-4" /> {selectedLender.phone}
                    </span>
                  </span>
                  {selectedLender.website && (
                    <a 
                      href={selectedLender.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-primary hover:underline"
                    >
                      <Globe className="h-4 w-4" /> {selectedLender.website}
                    </a>
                  )}
                </DialogDescription>
              </DialogHeader>

              {/* Notes Section */}
              {selectedLender.notes && (
                <div className="bg-muted p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Notes</h4>
                  <p className="text-sm whitespace-pre-wrap">{selectedLender.notes}</p>
                </div>
              )}

              {/* Product Guidelines Section */}
              {selectedLender.product_guidelines && (
                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-blue-800 dark:text-blue-300">
                    <FileText className="h-4 w-4" />
                    Product Guidelines & Offerings
                  </h4>
                  <p className="text-sm whitespace-pre-wrap text-blue-900 dark:text-blue-200">{selectedLender.product_guidelines}</p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                    💡 The AI assistant searches these guidelines when recommending lenders
                  </p>
                </div>
              )}

              {/* Programs extracted from Notes (AI) */}
              <NotesProgramsPanel lender={selectedLender} isAdmin={isAdmin} onChanged={refreshSelected} />

              {/* Program Documents Section */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Program Documents (PDFs)
                  </h4>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handlePdfUpload(selectedLender.lender_id, e.target.files[0]);
                        }
                      }}
                    />
                    <Button variant="outline" size="sm" disabled={uploadingPdf} asChild>
                      <span>
                        {uploadingPdf ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                        ) : (
                          <><Upload className="h-4 w-4 mr-2" /> Upload PDF</>
                        )}
                      </span>
                    </Button>
                  </label>
                </div>

                {selectedLender.program_documents && selectedLender.program_documents.length > 0 ? (
                  <div className="space-y-3">
                    {selectedLender.program_documents.map((doc) => (
                      <div key={doc.doc_id} className="border rounded-lg p-3 bg-background">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className="h-5 w-5 text-red-500" />
                            <div>
                              <p className="font-medium text-sm">{doc.original_filename}</p>
                              <p className="text-xs text-muted-foreground">
                                {(doc.file_size / 1024).toFixed(1)} KB • 
                                Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleParseProgram(selectedLender.lender_id, doc.doc_id)}
                              disabled={parsingDoc === doc.doc_id}
                            >
                              {parsingDoc === doc.doc_id ? (
                                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Parsing...</>
                              ) : (
                                <><Brain className="h-4 w-4 mr-1" /> {doc.ai_parsed ? 'Re-parse' : 'Parse with AI'}</>
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteProgram(selectedLender.lender_id, doc.doc_id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Extracted programs: review + approve into Product Matrix */}
                        {doc.ai_parsed && doc.parsed_programs && doc.parsed_programs.length > 0 && doc.review_status !== 'approved' && (
                          <ProgramReviewTable
                            key={`${doc.doc_id}-${doc.parsed_at}`}
                            lenderId={selectedLender.lender_id}
                            doc={doc}
                            onApproved={async () => {
                              const updated = await lendersAPI.getOne(selectedLender.lender_id);
                              setSelectedLender(updated.data);
                              fetchLenders();
                            }}
                          />
                        )}
                        {doc.review_status === 'approved' && (
                          <p className="mt-2 text-xs text-green-600" data-testid={`doc-approved-${doc.doc_id}`}>
                            ✓ {doc.approved_program_ids?.length || 0} program(s) approved into the Product Matrix on {new Date(doc.approved_at).toLocaleDateString()}
                          </p>
                        )}
                        {doc.ai_parsed && doc.review_status === 'nothing_found' && (
                          <p className="mt-2 text-xs text-amber-600">No loan programs could be identified in this document.</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No program documents uploaded yet. Upload a PDF to let AI extract loan programs.
                  </p>
                )}
              </div>

              {/* Manual Product Matrix */}
              <ProductMatrix 
                lenderId={selectedLender.lender_id}
                programs={selectedLender.product_matrix || []}
                onUpdate={(programs) => handleProductMatrixUpdate(selectedLender.lender_id, programs)}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, lender: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Lender
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-semibold">{deleteDialog.lender?.lender_name}</span>
              ? This will also remove all loan programs associated with this lender. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, lender: null })}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteLender(deleteDialog.lender?.lender_id)}
            >
              Delete Lender
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Lenders;
